import { useEffect } from 'react';

const SITE = 'Biznesi';

/**
 * The public origin search engines index (`VITE_PLATFORM_ORIGIN`, https://biznesi.al in
 * production). Canonical and Open Graph URLs are always built on it, never on the current
 * `location`, so a preview host or a localhost tab never becomes the canonical address.
 */
export const SITE_ORIGIN = ((import.meta.env.VITE_PLATFORM_ORIGIN as string | undefined) || 'https://biznesi.al').replace(/\/+$/, '');

export function absoluteUrl(path: string): string {
  return /^https?:\/\//i.test(path) ? path : `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Breadcrumb trail as schema.org `BreadcrumbList`; the first crumb is normally the home page. */
export function breadcrumbJsonLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, item: absoluteUrl(item.path) })),
  };
}

const OG_LOCALES: Record<string, string> = { al: 'sq_AL', sq: 'sq_AL', en: 'en_US', it: 'it_IT' };

function setMeta(attribute: 'name' | 'property', key: string, content: string | null) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (content === null) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function setCanonical(href: string | null) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function setJsonLd(data: unknown) {
  // The build-time snapshot (scripts/seo-prerender.mjs) writes the same id, so a page never
  // carries two copies of its own structured data once React has taken over.
  document.head.querySelectorAll('script[data-page-jsonld]').forEach((node) => node.remove());
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.pageJsonld = 'true';
  script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
  document.head.appendChild(script);
}

export interface PageMetaOptions {
  description?: string | null;
  noindex?: boolean;
  /** Canonical path on the public origin (`/products/restaurant`). Omitted → no canonical. */
  path?: string | null;
  /** Absolute or site-relative image for link previews. */
  image?: string | null;
  /** schema.org object(s) for this page; site-wide WebSite/Organization stay in index.html. */
  jsonLd?: unknown;
  /** The title is complete already; do not append " | Biznesi". */
  fullTitle?: boolean;
  /** UI language code (al/en/it) for `og:locale`. */
  lang?: string;
}

/**
 * Per-route document title, description, canonical URL, Open Graph tags, robots directive and
 * structured data. Private screens (account, workspace, sign-in) pass `noindex` so a crawler
 * that follows a stray link never lists them. Values are replaced by the next page that sets
 * its own.
 */
export function usePageMeta(title: string | null | undefined, options: PageMetaOptions = {}) {
  const { description, noindex = false, path, image, jsonLd, fullTitle = false, lang } = options;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    if (!title) return;
    const full = fullTitle || title === SITE ? title : `${title} | ${SITE}`;
    document.title = full;
    setMeta('property', 'og:title', full);
    setMeta('name', 'twitter:title', full);
    if (description) {
      const text = description.length > 160 ? `${description.slice(0, 157).trimEnd()}…` : description;
      setMeta('name', 'description', text);
      setMeta('property', 'og:description', text);
      setMeta('name', 'twitter:description', text);
    }
    setMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');
    const canonical = !noindex && path ? absoluteUrl(path) : null;
    setCanonical(canonical);
    setMeta('property', 'og:url', canonical);
    if (image) setMeta('property', 'og:image', absoluteUrl(image));
    if (lang) setMeta('property', 'og:locale', OG_LOCALES[lang] ?? 'sq_AL');
    setJsonLd(noindex ? null : jsonLd);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- jsonLd is compared by value through jsonLdKey
  }, [title, description, noindex, path, image, jsonLdKey, fullTitle, lang]);
}
